/**
 * Quotation Hooks - React hooks for quotation data management
 * Epic 25: Árajánlat kezelés
 */

import {
  acceptQuotation,
  convertToWorksheet,
  createNewQuotationVersion,
  createQuotation,
  deductAdvanceFromInvoice,
  deleteQuotation,
  getQuotationById,
  getQuotationVersionHistory,
  getQuotations,
  recordAdvancePayment,
  refundAdvancePayment,
  rejectQuotation,
  sendQuotation,
  updateQuotation,
  type Quotation,
  type QuotationFilters,
  type QuotationListResponse,
  type QuotationVersionHistoryResponse,
} from '@/api/quotations';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface UseQuotationsResult {
  quotations: Quotation[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseQuotationResult {
  quotation: Quotation | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseQuotationMutationsResult {
  createQuotation: typeof createQuotation;
  updateQuotation: typeof updateQuotation;
  deleteQuotation: typeof deleteQuotation;
  sendQuotation: typeof sendQuotation;
  acceptQuotation: typeof acceptQuotation;
  rejectQuotation: typeof rejectQuotation;
  convertToWorksheet: typeof convertToWorksheet;
  recordAdvancePayment: typeof recordAdvancePayment;
  refundAdvancePayment: typeof refundAdvancePayment;
  deductAdvanceFromInvoice: typeof deductAdvanceFromInvoice;
  /** SRV-FG-2-2: Create new version from existing quote */
  createNewVersion: typeof createNewQuotationVersion;
  isLoading: boolean;
  error: string | null;
}

/** SRV-FG-2-2: Hook for fetching version history */
interface UseQuotationVersionsResult {
  versions: QuotationVersionHistoryResponse['data'] | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ============================================
// HOOKS
// ============================================

export function useQuotations(filters: QuotationFilters = {}): UseQuotationsResult {
  const { t } = useTranslation();
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQuotations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: QuotationListResponse = await getQuotations(filters);
      setQuotations(response.data);
      setTotal(response.meta.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.quotations.loadAll');
      setError(message);
      console.error('Error fetching quotations:', err);
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.search,
    filters.status,
    filters.dateFrom,
    filters.dateTo,
    filters.page,
    filters.pageSize,
  ]);

  useEffect(() => {
    void fetchQuotations();
  }, [fetchQuotations]);

  return { quotations, total, isLoading, error, refetch: fetchQuotations };
}

export function useQuotation(id: string | undefined): UseQuotationResult {
  const { t } = useTranslation();
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQuotation = useCallback(async () => {
    if (!id) {
      setQuotation(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getQuotationById(id);
      setQuotation(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.quotations.loadOne');
      setError(message);
      console.error('Error fetching quotation:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchQuotation();
  }, [fetchQuotation]);

  return { quotation, isLoading, error, refetch: fetchQuotation };
}

export function useQuotationMutations(): UseQuotationMutationsResult {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wrap =
    <T extends unknown[], R>(fn: (...args: T) => Promise<R>) =>
    async (...args: T): Promise<R> => {
      setIsLoading(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.common.operationFailed');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

  return {
    createQuotation: wrap(createQuotation),
    updateQuotation: wrap(updateQuotation),
    deleteQuotation: wrap(deleteQuotation),
    sendQuotation: wrap(sendQuotation),
    acceptQuotation: wrap(acceptQuotation),
    rejectQuotation: wrap(rejectQuotation),
    convertToWorksheet: wrap(convertToWorksheet),
    recordAdvancePayment: wrap(recordAdvancePayment),
    refundAdvancePayment: wrap(refundAdvancePayment),
    deductAdvanceFromInvoice: wrap(deductAdvanceFromInvoice),
    createNewVersion: wrap(createNewQuotationVersion),
    isLoading,
    error,
  };
}

/**
 * SRV-FG-2-2: Hook for quotation version history
 */
export function useQuotationVersions(id: string | undefined): UseQuotationVersionsResult {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<QuotationVersionHistoryResponse['data'] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVersions = useCallback(async () => {
    if (!id) {
      setVersions(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getQuotationVersionHistory(id);
      setVersions(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.common.operationFailed');
      setError(message);
      console.error('Error fetching version history:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchVersions();
  }, [fetchVersions]);

  return { versions, isLoading, error, refetch: fetchVersions };
}
